import { useOnboardingContext } from '../contexts/OnboardingContext'
import { FlowType, FlowVariant } from '../types/onboarding.types'
import { authService } from '../services/authService'
import { getNextRepeatUrl } from '../utils/repeatFlow'
import { useNavigate } from 'react-router-dom'
import { useState } from 'react'
import toast from 'react-hot-toast'

export function useFlowSelectionLogic() {
  const { initializeFlow, refreshProgress } = useOnboardingContext()
  const navigate = useNavigate()
  const [isInitializing, setIsInitializing] = useState(false)

  const triggerFlowSelection = async (
    flowVariant: FlowVariant,
    options?: { isFirstRequest?: boolean }
  ) => {
    // Por defecto se asume 1ra solicitud (comportamiento de siempre) para
    // no afectar a los demás llamadores (FlowSelectionPage, FlowTestPage),
    // que no pasan este flag.
    const isFirstRequest = options?.isFirstRequest ?? true

    setIsInitializing(true)
    try {
      await initializeFlow(FlowType.LEASING, flowVariant)
      await refreshProgress()
      toast.success('Flujo inicializado correctamente')

      // Se obtiene primero el creditAppId: además de servir para el
      // progressURL de abajo, refresca el prospectoQuoteId en localStorage
      // (efecto secundario de getCurrentCreditApplicationId) que
      // updateProspectoQuoteEstatus necesita leer ya actualizado — si se
      // pidiera después, podría quedarse con el de una solicitud anterior.
      let creditAppId: string | null = null
      try {
        creditAppId = await authService.getCurrentCreditApplicationId()
      } catch {
        // No bloquear el flujo si falla esta consulta.
      }

      let newProgressURL: string
      if (isFirstRequest) {
        newProgressURL =
          flowVariant === FlowVariant.PERSONA_MORAL
            ? '/onboarding/application-start?flow=persona_moral'
            : '/onboarding/application-start?flow=persona_fisica'
      } else {
        // A partir de la 2da solicitud se salta ApplicationStartPage y el
        // resto de pasos intermedios — va directo a la primera pantalla que
        // consultasStatus.screens marque como true (ver utils/repeatFlow).
        // Como se salta esa pantalla, se replica aquí su efecto de estatus
        // (además del progressURL, que se actualiza igual que siempre más
        // abajo).
        let screens: Record<string, boolean> | null = null
        try {
          screens = (await authService.getRepeatFlowInfo()).screens
        } catch {
          // Sin screens se usa el recorrido abreviado por defecto.
        }
        newProgressURL = getNextRepeatUrl(
          null,
          flowVariant !== FlowVariant.PERSONA_MORAL,
          screens
        )
        try {
          await authService.updateProspectoQuoteEstatus('Datos Básicos')
        } catch {
          // No bloquear el flujo si falla esta actualización.
        }
      }

      try {
        if (creditAppId) {
          await authService.updateCreditApplicationProgress(
            creditAppId,
            newProgressURL
          )
        }
      } catch {}
      navigate(newProgressURL)
    } catch (error) {
      toast.error(
        'Error al inicializar el flujo. Por favor intenta nuevamente.'
      )
    } finally {
      setIsInitializing(false)
    }
  }

  return { triggerFlowSelection, isInitializing }
}
